import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiFileText, FiInfo, FiPlus, FiX, FiMove } from 'react-icons/fi';
import axios from 'axios';
import ImageUploader from '../components/ImageUploader';
import ProcessingLoader from '../components/ProcessingLoader';
import './ToolPage.css';

const ImageToPdf = () => {
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageSize, setPageSize] = useState('a4');
  const [orientation, setOrientation] = useState('portrait');

  const handleFileSelect = (selectedFiles) => {
    if (Array.isArray(selectedFiles)) {
      setFiles(selectedFiles);
    } else if (selectedFiles) {
      setFiles([selectedFiles]);
    }
    setPdfUrl(null);
    setError(null);
  };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  const moveFile = (index, direction) => {
    const newFiles = [...files];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < files.length) {
      [newFiles[index], newFiles[newIndex]] = [newFiles[newIndex], newFiles[index]];
      setFiles(newFiles);
    }
  };

  const handleProcess = async () => {
    if (files.length === 0) return;

    setIsProcessing(true);
    setError(null);

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('page_size', pageSize);
    formData.append('orientation', orientation);

    try {
      const response = await axios.post('/api/image-to-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob'
      });

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create PDF. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `images-${Date.now()}.pdf`;
    link.click();
  };

  const handleReset = () => {
    setFiles([]);
    setPdfUrl(null);
    setError(null);
  };

  return (
    <div className="tool-page">
      {isProcessing && (
        <ProcessingLoader 
          message="Creating PDF..."
          subMessage="Combining your images into a PDF document"
        />
      )}

      <div className="tool-hero">
        <div className="container">
          <motion.div 
            className="tool-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="tool-icon gradient-warning">
              <FiFileText />
            </div>
            <h1>Image to PDF</h1>
            <p>Convert single or multiple images into a PDF document. Perfect for documents, 
               presentations, and portfolios.</p>
          </motion.div>
        </div>
      </div>

      <div className="tool-content">
        <div className="container">
          {!pdfUrl ? (
            <motion.div 
              className="tool-upload-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ImageUploader 
                onFileSelect={handleFileSelect}
                multiple={true}
                maxFiles={20}
                title="Upload images for PDF"
                subtitle="Add multiple images in your preferred order"
              />

              {files.length > 0 && (
                <motion.div 
                  className="file-list-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <h3>Images ({files.length})</h3>
                  <p className="file-list-hint">Drag to reorder pages</p>
                  <div className="file-list">
                    {files.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="file-item">
                        <span className="file-number">{index + 1}</span>
                        <img src={file.preview || URL.createObjectURL(file)} alt={file.name} />
                        <span className="file-name">{file.name}</span>
                        <div className="file-actions">
                          <button onClick={() => moveFile(index, -1)} disabled={index === 0}>
                            <FiMove />
                          </button>
                          <button onClick={() => removeFile(index)}>
                            <FiX />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {files.length > 0 && (
                <motion.div 
                  className="options-section"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="options-row">
                    <div className="option-group">
                      <label className="option-label">Page Size</label>
                      <select 
                        value={pageSize} 
                        onChange={(e) => setPageSize(e.target.value)}
                        className="option-select"
                      >
                        <option value="a4">A4</option>
                        <option value="letter">Letter</option>
                        <option value="legal">Legal</option>
                        <option value="a3">A3</option>
                        <option value="a5">A5</option>
                      </select>
                    </div>
                    <div className="option-group">
                      <label className="option-label">Orientation</label>
                      <select 
                        value={orientation} 
                        onChange={(e) => setOrientation(e.target.value)}
                        className="option-select"
                      >
                        <option value="portrait">Portrait</option>
                        <option value="landscape">Landscape</option>
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}

              {files.length > 0 && (
                <motion.div 
                  className="process-actions"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <button 
                    className="btn-process"
                    onClick={handleProcess}
                    disabled={isProcessing}
                  >
                    Create PDF
                  </button>
                </motion.div>
              )}

              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              className="pdf-result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="pdf-preview">
                <div className="pdf-icon">
                  <FiFileText size={60} />
                </div>
                <h3>PDF Created Successfully!</h3>
                <p>{files.length} images combined into one PDF</p>
              </div>
              <div className="result-actions">
                <button className="action-btn primary" onClick={handleDownload}>
                  Download PDF
                </button>
                <button className="action-btn secondary" onClick={handleReset}>
                  Create Another
                </button>
              </div>
            </motion.div>
          )}

          <div className="tool-info-section">
            <div className="info-card">
              <FiInfo className="info-icon" />
              <h3>How it works</h3>
              <p>Upload multiple images and arrange them in your preferred order. 
                 Choose your page size and orientation, then generate a professional PDF 
                 document ready for sharing or printing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageToPdf;
